<template>
  <section class="cs-details" :class="{ desktop: $isDesktop }">
    <aside class="circle" data-test="campsite-circle-image">
      <img
        v-if="photo"
        :src="`/_cfi/cdn-cgi/image/format=auto,fit=cover,quality=80,w=200,h=200${photo.url.masterImage}`"
        :srcset="`
            /_cfi/cdn-cgi/image/format=auto,fit=cover,quality=80,w=200,h=200${photo.url.masterImage} 200w,
            /_cfi/cdn-cgi/image/format=auto,fit=cover,quality=70,w=320,h=320${photo.url.masterImage} 320w,
            /_cfi/cdn-cgi/image/format=auto,fit=cover,quality=60,w=480,h=480${photo.url.masterImage} 480w
          `"
        :alt="photoCaptionAltFormatter(photo.caption) || campsiteName"
        sizes="160px"
        width="160"
        height="160"
      />
    </aside>
    <div class="details">
      <translate v-if="prettyId" tag="p" class="booking-id" :tt="{ prettyId }">
        Booking ID: {prettyId}
      </translate>
      <h2 class="cs-name" data-test="campsiteName">
        {{ campsiteName }}
      </h2>
      <pu-rating-bubble
        v-if="rating"
        :rating="rating.rate"
        :rate-count="rating.votes"
      />
      <p class="cs-address">
        {{ address }}
      </p>
      <p v-if="arrive && depart" class="dates">
        <template v-if="isSameMonth">
          <search-summary-bar-details-calendar-same-month
            :date-range="{ arrive, depart }"
          />
        </template>
        <template v-else>
          <i18n-t keypath="{fromDate} - {toDate}">
            <template #fromDate>
              <pu-utc-date :date="arrive" options="dayMonth" />
            </template>
            <template #toDate>
              <pu-utc-date :date="depart" options="dayMonth" />
            </template>
          </i18n-t>
        </template>
        <pu-date tag="span" class="year" :date="depart" options="year" />
      </p>
      <p v-if="pitchName" class="pitch-name">
        {{ pitchName }}
      </p>
      <last-booked v-if="lastBooked" :date="lastBooked" class="last-booked" />
    </div>
  </section>
</template>

<script lang="ts">
import { CampsiteDetails } from '~/apps/campsite-details/types'
import { photoCaptionAltFormatter } from '~/utility/photoCaptionAltFormatter'
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'
import { Rating } from '../types'

@Component
class BookingCampsiteDetails extends Vue {
  @Prop()
  address: string

  @Prop()
  campsiteName: string

  @Prop()
  pitchName: string

  @Prop()
  arrive: string

  @Prop()
  depart: string

  @Prop()
  photo: CampsiteDetails['primaryPhoto']

  @Prop()
  photoCaption: string

  @Prop()
  rating: Rating

  @Prop()
  lastBooked: string

  @Prop()
  prettyId: string

  get isSameMonth() {
    const arrive = new Date(this.arrive)
    const depart = new Date(this.depart)
    return arrive.getMonth() === depart.getMonth()
  }

  photoCaptionAltFormatter(caption) {
    return photoCaptionAltFormatter(caption)
  }
}

export default toNative(BookingCampsiteDetails)
</script>

<style scoped>
.cs-details {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  padding: 0 1.4em 1.4em;
  background: white;
}

.circle {
  position: relative;
  margin: 0 1em 0 -4em;
  background: rgba(15, 30, 65, 0.125);
  border-radius: 50%;
  display: block;
  overflow: hidden;
  width: 8em;
  padding-top: 8em;
  height: 0;
  flex-shrink: 0;
}

.circle img {
  height: auto;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
}

.circle::after {
  border-radius: inherit;
  box-shadow: inset 0 0 0 1px rgba(15, 30, 65, 0.1);
  content: '';
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
}

@supports (object-fit: cover) {
  .circle img {
    object-fit: cover;
    height: 100%;
  }
}

.details {
  flex-grow: 1;
}

.cs-details p {
  margin: 0.5em 0 0 0;
  font-size: var(--body);
}

.cs-details :deep(.rating-bubble) {
  font-size: 0.8em;
}

.cs-details :deep(.rating-bubble:before) {
  content: '';
}

.cs-details .pitch-name {
  margin-top: 0.1em;
}

.cs-name {
  font-size: 1.2em;
  line-height: 1.2;
  margin: 0.3em 0 0.2em 0;
  overflow-wrap: anywhere;
  hyphens: auto;
}

.last-booked {
  margin: 2em 0 0 -0.5em;
}

.dates {
  border-top: 1px solid var(--colour-page-background-light);
  padding-top: 0.5em;
}

/* desktop & tablet */
.desktop .circle {
  width: 10em;
  padding-top: 10em;
  margin: 0 1.4em 0 0;
}
.desktop .cs-name {
  font-size: revert;
}

.cs-details.desktop p {
  font-size: inherit;
}

.cs-details.desktop :deep(.rating-bubble) {
  font-size: inherit;
}
</style>
